import React, { useEffect, useState } from 'react';
import { Field } from 'formik';
import { useTranslation } from 'react-i18next';

import './input.css';

import { DropdownMenu } from '../Dropdown-menu/Dropdown-menu';
import { InputProps } from './types';
import { Icon, Loading, PopperComponent } from '../../UI';
import { PopperOptions } from '../../UI/Popper/Popper';
import { cleanNumber, numberWithCommas } from '../../../helpers/functions';

const width = {
  full: 'w-full',
  large: 'w-96',
  medium: 'w-64',
  small: 'w-44',
};

const passwordHintOptions: PopperOptions = {
  placement: 'left',
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [0, 10],
      },
    },
    {
      name: 'flip',
      options: {
        fallbackPlacements: ['bottom-start', 'top-start'],
      },
    },
  ],
};

export const Input = ({
  autocomplete = 'off',
  autoFocus,
  id,
  name,
  size = 'full',
  type: propsType,
  assistiveText,
  className = '',
  dropdownDataName,
  dropdownItems,
  dropdownItemsComponents,
  dropdownItemsData,
  dropdownClassname,
  errorMessage,
  formik,
  isDisabled,
  label,
  loading,
  max,
  min,
  onBlur,
  onFocus,
  placeholder,
  prefix,
  suffix,
  readOnly,
  showLabel,
  showPasswordHint,
  validate,
  value,
  onChange,
  onKeyUp,
}: InputProps) => {
  const [passwordShown, setPasswordShown] = useState(false);
  const [passwordHintShown, setPasswordHintShown] = useState(false);
  const [labelShown, setLabelShown] = useState(showLabel);
  const [type, setType] = useState(propsType);
  const { t } = useTranslation();

  const passwordValidations = [
    'length',
    'lowercase',
    'uppercase',
    'numbers',
    'specialChars',
  ];

  const finalSize = width[size];

  const fieldValue = formik ? formik.values[name] : value ? value : undefined;

  useEffect(() => {
    if (
      showLabel ||
      (fieldValue !== undefined && fieldValue.toString().length > 0)
    ) {
      setLabelShown(true);
    } else {
      setLabelShown(false);
    }
  }, [fieldValue]);

  const handlePasswordIconClick = () => {
    setPasswordShown((shown) => !shown);
    setType((type) => (type === 'password' ? 'text' : 'password'));
  };

  const handlePaste = (e: any) => {
    if (
      formik &&
      formik?.values[name]?.length &&
      (Math.abs(formik.values[name].length - e.target.value.length) > 1 ||
        (formik.values[name].length === e.target.value.length &&
          formik.values[name] !== e.target.value)) &&
      type === 'password' &&
      showPasswordHint
    ) {
      setTimeout(() => setPasswordHintShown(true), 500);
    }
  };

  const handleChange = (e: any) => {
    if (onChange) {
      if (type === 'formattedNumber') {
        onChange(name, numberWithCommas(e.target.value.replace(/,/g, '')));
      } else {
        onChange(name, e.target.value);
      }
    }

    if (formik?.handleChange) {
      if (type === 'formattedNumber') {
        //add commas in quantity every three digits
        e.target.value = numberWithCommas(e.target.value.replace(/,/g, ''));

        formik.handleChange(e);
      } else {
        formik.handleChange(e);
      }
    }

    // for chrome password suggestions
    handlePaste(e);
  };

  const handleKeyUp = (e) => {
    if (onKeyUp) {
      onKeyUp(e);
    }
  };

  const handleFocus = () => {
    setLabelShown(true);

    if (onFocus) {
      onFocus();
    }

    if (type === 'password' && showPasswordHint) {
      setPasswordHintShown(true);
    }
  };

  const handleBlur = (e) => {
    if (!formik && value === undefined) {
      setLabelShown(false);
    }
    if (
      formik &&
      formik.values[name] !== undefined &&
      formik.values[name]?.length === 0
    ) {
      setLabelShown(false);
    }

    if (onBlur) {
      onBlur();
    }

    if (formik?.handleBlur) {
      formik.handleBlur(e);
    }

    if (type === 'password') {
      setPasswordHintShown(false);
    }
  };

  const renderField = () => (
    <Field name={name} validate={validate}>
      {({ meta }) => {
        let field;
        switch (type) {
          case 'textarea':
            field = renderTextArea(meta);
            break;
          case 'dropdown':
          case 'searchDropdown':
            field = renderDropdown();
            break;
          default:
            field = renderInput(meta);
        }

        return (
          <>
            {field}
            {prefix && (
              <span
                className={`absolute left-0 top-0 pt-1 ${
                  isDisabled ? 'text-grey-30' : 'text-grey-50'
                }`}
              >
                {prefix}
              </span>
            )}
            {suffix && (
              <span
                className={`absolute right-0 bottom-0 pb-2 ${
                  isDisabled ? 'text-grey-30' : 'text-grey-50'
                }`}
              >
                {suffix}
              </span>
            )}
          </>
        );
      }}
    </Field>
  );

  const renderInput = (meta) => (
    <>
      <input
        autoComplete={autocomplete}
        autoFocus={autoFocus}
        type={['number', 'email', 'password'].includes(type) ? type : 'text'}
        id={id}
        name={name}
        readOnly={readOnly}
        value={fieldValue}
        placeholder={!labelShown ? placeholder : ''}
        min={min}
        max={max}
        className={`
          ${className}
          pb-2
          mt-1.5
          w-full
          border-solid
          border-b
          rounded-none
          bg-white
          dark:bg-grey-90
          dark:text-white
          dark:border-white
          ${
            isDisabled
              ? 'border-grey-30'
              : formik && meta.touched
              ? formik && meta.error
                ? 'border-negative'
                : 'border-positive'
              : 'border-black'
          }
          ${isDisabled ? 'text-grey-30' : 'text-grey-70'}
          ${prefix && 'pl-4'}
        `}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyUp={handleKeyUp}
        onPaste={handlePaste}
        disabled={isDisabled}
      />
      <Loading
        size={'small'}
        status={loading}
        className="bg-white dark:bg-black absolute bottom-2.5 -right-0.5 text-black text-lg pointer-events-none dark:border-grey-70 dark:text-white"
      />
    </>
  );

  const renderTextArea = (meta) => (
    <>
      <textarea
        id={id}
        name={name}
        value={formik.values[name]}
        placeholder={!labelShown ? placeholder : ''}
        className={`
        ${className}
        p-2
        mt-1.5
        w-full
        border-solid
        border
        rounded-xl
        align-top
        text-grey-70
        dark:bg-grey-90
        dark:text-white
        ${
          meta.touched
            ? meta.error
              ? 'border-negative'
              : 'border-positive'
            : 'border-black dark:border-white'
        }
      `}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyUp={handleKeyUp}
        disabled={isDisabled}
      />
      <Loading
        size={'small'}
        status={loading}
        className="bg-white dark:bg-black absolute bottom-2.5 -right-0.5 text-black text-lg pointer-events-none dark:border-grey-70 dark:text-white"
      />
    </>
  );

  const renderDropdown = () => {
    const filteredDropdownItems: string[] = [];
    const filteredDropdownItemsData: any[] = [];
    const filteredDropdownItemsComponents: any[] = [];

    if (type === 'searchDropdown') {
      dropdownItems &&
        dropdownItems.forEach((item, index) => {
          if (item.toLowerCase().includes(formik.values[name].toLowerCase())) {
            filteredDropdownItems.push(item);

            if (dropdownItemsData && index < dropdownItemsData.length) {
              filteredDropdownItemsData.push(dropdownItemsData[index]);
            }

            if (
              dropdownItemsComponents &&
              index < dropdownItemsComponents.length
            ) {
              filteredDropdownItemsComponents.push(
                dropdownItemsComponents[index]
              );
            }
          }
        });

      dropdownItems = filteredDropdownItems;
      dropdownItemsData = filteredDropdownItemsData;
      dropdownItemsComponents = filteredDropdownItemsComponents;
    }

    return (
      <DropdownMenu
        isDropdownDisabled={isDisabled}
        dropdownIconClassName="relative"
        dropdownTooltipClassName={`
          bg-white
          border-solid
          border-grey-10
          border
          shadow-md
          z-20
          ${finalSize}
          ${dropdownClassname}
          max-h-46
        `}
        dropdownTooltipXOffset={finalSize}
        dropdownItems={dropdownItems || []}
        dropdownItemsData={dropdownItemsData}
        dropdownItemsComponents={dropdownItemsComponents}
        handleClick={(item, data) => {
          setLabelShown(true);
          formik.setFieldValue(name, item);
          if (dropdownDataName) {
            formik.setFieldValue(dropdownDataName, data);
          }
        }}
        dropdownArrowClassName="absolute right-1 bottom-3 text-black"
        showArrow={type === 'dropdown'}
        hideOnItemClick
        hideOnOutsideClick={type === 'searchDropdown'}
        // disableCursorOverride={type === 'searchDropdown'}
      >
        <input
          disabled={isDisabled}
          autoComplete={autocomplete}
          type={type}
          id={id}
          name={name}
          value={formik.values[name]}
          placeholder={!labelShown ? placeholder : ''}
          className={`
            ${className}
            pb-2
            mt-1.5
            w-full
            border-solid
            border-b
            bg-white
            dark:bg-grey-90
            dark:text-white
            dark:border-white
            ${
              isDisabled
                ? 'border-grey-30'
                : formik.values[name]?.length > 0
                ? 'border-positive'
                : 'border-black'
            }
            ${isDisabled ? 'text-grey-30' : 'text-grey-70'}
            ${type === 'dropdown' && 'cursor-pointer'}
          `}
          onChange={(event) => {
            handleChange(event);
            if (dropdownItems && dropdownItemsData && dropdownDataName) {
              const index = dropdownItems.indexOf(event.target.value);
              if (index !== -1) {
                formik.setFieldValue(
                  dropdownDataName,
                  dropdownItemsData[index]
                );
              } else {
                formik.setFieldValue(dropdownDataName, null);
              }
            }
          }}
          readOnly={type !== 'searchDropdown'}
          onFocus={type === 'searchDropdown' ? handleFocus : () => {}}
          onBlur={type === 'searchDropdown' ? handleBlur : () => {}}
          onKeyUp={type === 'searchDropdown' ? handleKeyUp : () => {}}
        />
        {type === 'searchDropdown' && (
          <>
            <Icon
              className=" absolute bottom-1 -right-0.5 text-black dark:text-white pointer-events-none mb-2 mr-1"
              color="black"
              svgName={'search'}
            />
          </>
        )}
        <Loading
          size={'small'}
          status={loading}
          className="bg-white dark:bg-black absolute bottom-2.5 -right-0.5 text-black text-lg pointer-events-none dark:bg-grey-90 dark:text-white"
        />
      </DropdownMenu>
    );
  };

  const renderPasswordHint = () => (
    <div className="w-64 text-left h-max border-solid border-grey-10 p-4 border z-10 bg-white dark:bg-black-real dark:text-white rounded-xl shadow-md password-hint">
      <h5 className="text-black dark:text-white text-sm font-semibold uppercase">
        {t('input.passwordHint.title')}
      </h5>
      <p className="text-grey-50 text-sm mt-2">
        {t('input.passwordHint.description')}
      </p>
      <div className="mt-5 -mb-3">
        {passwordValidations.map((v, index) => {
          return (
            <div className="flex items-center mb-3" key={`validation-${v}`}>
              <Icon
                svgName={'check'}
                className={`
                  ${
                    formik.errors[name] &&
                    formik.errors[name].split(':').includes(v)
                      ? 'bg-grey-30'
                      : 'bg-primary'
                  }
                  text-white
                  dark:text-black
                  rounded-full
                  w-4 h-4 p-1
                `}
              />
              <span className="text-sm text-black-2 ml-2">
                {t('input.passwordHint.' + v)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderFinalContent = (
    <>
      <div className="relative">
        {labelShown && (
          <label
            htmlFor={id}
            className={`absolute -top-4 left-0 text-xs font-medium ${
              isDisabled
                ? 'text-grey-30 dark:text-grey-70'
                : 'text-black dark:text-white'
            } `}
          >
            {label}
          </label>
        )}
        {renderField()}
        <div className="relative w-full">
          {errorMessage ? (
            <span className={'absolute left-0 top-1 text-negative text-xs'}>
              {errorMessage}
            </span>
          ) : (
            <></>
          )}
        </div>
        {type === 'password' || passwordShown ? (
          <Icon
            svgName={passwordShown ? 'eyeDisabled' : 'eye'}
            className="absolute bottom-2 right-0 text-base cursor-pointer"
            onClick={handlePasswordIconClick}
          />
        ) : (
          <></>
        )}
      </div>
      <small className="pt-2 block text-xs w-full text-left text-grey-50">
        {assistiveText}
      </small>
    </>
  );

  return (
    <div className={`flex-col flex ${finalSize}`}>
      {type === 'password' && showPasswordHint ? (
        <PopperComponent
          options={passwordHintOptions}
          reference={renderFinalContent}
          visible={passwordHintShown}
        >
          {passwordHintShown ? renderPasswordHint() : null}
        </PopperComponent>
      ) : (
        renderFinalContent
      )}
    </div>
  );
};
